/* pod-cart-previews.js — the customer's own design on every POD cart line.
 *
 * Theme asset (docs/theme-patches/cart-preview-cartjs.md). Loaded by
 * snippets/cart-drawer.liquid and sections/cart-template.liquid.
 *
 * The Liquid in those files already swaps a POD line's thumbnail for the
 * line's _preview_image (the 2026-08 patch), with one retry 2.5 s later and
 * then the product's stock image. On prod on 2026-10-17 every POD line in the
 * drawer showed the stock image: three knit lines whose preview.png was
 * already public, and a Photo Sweatshirt line. This script does not depend on
 * that Liquid branch: it reads the cart's own line properties from /cart.js
 * (the same _preview_image the checkout extension and thank-you page show)
 * and puts the preview on the row once it has actually loaded. Until then,
 * and for good if it never loads, the row keeps what the theme rendered (the
 * stock image), so a missing file can never leave a broken or empty tile.
 *
 * Bounded: per row at most MAX_ATTEMPTS loads, waits DELAYS_MS between them,
 * each load abandoned after LOAD_TIMEOUT_MS. Worst case per row:
 * 2 + 4 + 8 = 14 s of waits plus 4 x 10 s of loads = 54 s, then it stops.
 * The /cart.js read is abandoned at 10 s (and aborted), so a hung request
 * never blocks the next run.
 *
 * Diagnostics on each POD row (read them in the console after a push):
 *   data-pod-preview-via    liquid | cart-js  (who marked the row as POD)
 *   data-pod-preview-state  trying | shown | stamped | fallback
 */
(function () {
  'use strict';
  if (window.PodCartPreviews) return;   // both cart files load it; run once

  var PROPERTY = '_preview_image';
  var MAX_ATTEMPTS = 4;
  var DELAYS_MS = [0, 2000, 4000, 8000];
  var LOAD_TIMEOUT_MS = 10000;
  var DEBOUNCE_MS = 150;
  var CART_TIMEOUT_MS = 10000;   // a /cart.js that has not answered by then: the theme's images stay
  // The containers whose rows carry data-key="{{ item.key }}".
  var ROWS = '#CartDrawer-CartItems [data-key], #main-cart-items [data-key]';
  var WATCH = 'cart-drawer, cart-items, cart-drawer-items';

  function root() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof r === 'string' && r ? r : '/';
  }

  function previewOf(item) {
    var p = item && item.properties;
    var url = p && p[PROPERTY];
    return typeof url === 'string' && url.indexOf('https://') === 0 ? url : null;
  }

  function base(url) { return String(url || '').split('?')[0].split('#')[0]; }

  function withAttempt(url, n) {
    if (!n) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'pod_try=' + n;
  }

  function show(img, src) {
    img.onerror = null;
    img.removeAttribute('onerror');
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = src;
    img.alt = 'Your personalized design';
    // Inline !important, transition off first: the prod theme's opacity
    // transition otherwise keeps it at 0 (cart-preview-swap.md, trap 3).
    img.style.setProperty('transition', 'none', 'important');
    img.style.setProperty('opacity', '1', 'important');
    var box = img.closest('.pod-preview-box');
    if (box) box.classList.add('pod-loaded');
  }

  /** One row: load the preview off-screen, then swap it in; bounded retries. */
  function ensure(row, url) {
    var img = row.querySelector('.cart__image-box img, img.cart__image');
    if (!img) return;
    if (!row.hasAttribute('data-pod-preview-via')) {
      row.setAttribute('data-pod-preview-via', img.closest('.pod-preview-box') ? 'liquid' : 'cart-js');
    }
    // The PDP's own stamp (the approved pixels, or the knitted frame) is
    // newer than any file: leave it.
    var current = img.getAttribute('src') || '';
    if (current.indexOf('data:') === 0 || img.hasAttribute('data-pod-design')) {
      row.setAttribute('data-pod-preview-state', 'stamped');
      return;
    }
    var state = row.getAttribute('data-pod-preview-state');
    if (state === 'trying' || state === 'shown' || state === 'fallback') return;
    if (base(current) === base(url) && img.complete && img.naturalWidth > 0) {
      row.setAttribute('data-pod-preview-state', 'shown');
      return;
    }
    row.setAttribute('data-pod-preview-state', 'trying');

    function attempt(n) {
      if (!row.isConnected) return;   // re-rendered: the new row has its own run
      var probe = new window.Image();
      var settled = false;
      var guard = setTimeout(function () { done(false); }, LOAD_TIMEOUT_MS);
      function done(ok) {
        if (settled) return;
        settled = true;
        clearTimeout(guard);
        probe.onload = probe.onerror = null;
        if (!row.isConnected) return;
        var now = img.getAttribute('src') || '';
        if (now.indexOf('data:') === 0 || img.hasAttribute('data-pod-design')) {
          row.setAttribute('data-pod-preview-state', 'stamped');
          return;
        }
        if (ok) {
          show(img, probe.src);
          row.setAttribute('data-pod-preview-state', 'shown');
          return;
        }
        if (n + 1 < MAX_ATTEMPTS) {
          setTimeout(function () { attempt(n + 1); }, DELAYS_MS[n + 1]);
          return;
        }
        // Never loaded: the row keeps the theme's stock image.
        row.setAttribute('data-pod-preview-state', 'fallback');
      }
      probe.onload = function () { done(probe.naturalWidth > 0 || probe.width > 0); };
      probe.onerror = function () { done(false); };
      probe.src = withAttempt(url, n);
    }
    attempt(0);
  }

  var timer = null;
  var inFlight = null;
  var again = false;

  function run() {
    var rows = document.querySelectorAll(ROWS);
    if (!rows.length) return Promise.resolve(0);
    if (inFlight) { again = true; return inFlight; }
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var guard = null;
    var read = fetch(root() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: ctrl ? ctrl.signal : undefined })
      .then(function (res) { return res && res.ok ? res.json() : null; });
    var late = new Promise(function (resolve, reject) {
      guard = setTimeout(function () {
        if (ctrl) { try { ctrl.abort(); } catch (e) { /* settled */ } }
        reject(new Error('cart.js did not answer'));
      }, CART_TIMEOUT_MS);
    });
    inFlight = Promise.race([read, late])
      .then(function (cart) {
        clearTimeout(guard);
        var byKey = {};
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          var url = previewOf(items[i]);
          if (url && items[i].key) byKey[items[i].key] = url;
        }
        var touched = 0;
        var live = document.querySelectorAll(ROWS);
        for (var j = 0; j < live.length; j++) {
          var key = live[j].getAttribute('data-key');
          if (key && byKey[key]) { ensure(live[j], byKey[key]); touched++; }
        }
        return touched;
      })
      .catch(function () { clearTimeout(guard); return 0; })   // no cart answer (or too late): the theme's images stay
      .then(function (n) {
        inFlight = null;
        if (again) { again = false; schedule(); }
        return n;
      });
    return inFlight;
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(function () { timer = null; run(); }, DEBOUNCE_MS);
  }

  var watched = [];
  function watch() {
    if (!('MutationObserver' in window)) return;
    var els = document.querySelectorAll(WATCH);
    for (var i = 0; i < els.length; i++) {
      if (watched.indexOf(els[i]) !== -1) continue;
      watched.push(els[i]);
      // childList only: our own src swaps are attribute changes and never
      // re-trigger a run.
      new MutationObserver(schedule).observe(els[i], { childList: true, subtree: true });
    }
  }

  function start() {
    watch();
    schedule();
  }

  // The app's PDP announces its own adds; any other add re-renders the
  // drawer, which the observer sees.
  document.addEventListener('pod-pdp-cart-success', schedule);

  window.PodCartPreviews = {
    run: run,
    start: start,
    config: { MAX_ATTEMPTS: MAX_ATTEMPTS, DELAYS_MS: DELAYS_MS, LOAD_TIMEOUT_MS: LOAD_TIMEOUT_MS, DEBOUNCE_MS: DEBOUNCE_MS, CART_TIMEOUT_MS: CART_TIMEOUT_MS }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
